import { FC } from 'react';
import { useSetRecoilState } from 'recoil';
import { Card } from 'react-bootstrap';
import CloseIcon from '@mui/icons-material/Close';

import Button, { ButtonVariant } from 'components/common/Button/Button';
import Link from 'components/common/Link/Link';
import { pageAlertConfigState } from 'components/common/PageAlert/PageAlert.state';
import { PageAlertVariant } from 'components/common/PageAlert/PageAlert.types';
import { BillingPlanForm } from './BillingPlanForm';

import './BillingPlan.scss';

interface BillingPlanProps {
  isCreditCard: boolean;
  isCustomPlan: boolean;
  email: string;
  onClose: () => void;
  onAddCreditCard: () => void;
  hasCreditCard: boolean;
}

export const BillingPlan: FC<BillingPlanProps> = ({
  isCreditCard,
  isCustomPlan,
  email,
  onClose,
  onAddCreditCard,
  hasCreditCard,
}: BillingPlanProps) => {
  const setPageAlertConfig = useSetRecoilState(pageAlertConfigState);

  const handleBillingPlanUpdate = () => {
    onClose();
  };

  const handleError = (message: string): void => {
    setPageAlertConfig({
      message,
      variant: PageAlertVariant.ERROR,
      autoHide: true,
      duration: 3,
    });
  };

  const header = isCreditCard ? 'Change Plan' : 'Add Credit Card';

  function AddCreditCard(): JSX.Element {
    return (
      <>
        <p>Please add your credit card to update your plan.</p>
        <div className="BillingPlan__Footer">
          <Button variant={ButtonVariant.PRIMARY} onClick={onAddCreditCard}>
            {`${hasCreditCard ? 'Update' : 'Add'} Credit Card`}
          </Button>
        </div>
      </>
    );
  }

  function UpdateBilling(): JSX.Element {
    return (
      <>
        {isCustomPlan ? (
          <>
            <p className="BillingPlan__CustomPlan">
              You are currently on a custom plan. To discuss a new custom plan, please email
              <Link className="link" href="mailto:contact@scalyr.com">
                contact@scalyr.com
              </Link>
              .
            </p>
            <p>
              You can view our standard rates on our
              <Link className="link" openInNewTab href="/pricing">
                pricing page
              </Link>
              .
            </p>
          </>
        ) : (
          <BillingPlanForm
            email={email}
            onAddCreditCard={onAddCreditCard}
            onUpdate={handleBillingPlanUpdate}
            handleError={handleError}
          />
        )}
      </>
    );
  }
  return (
    <Card className="BillingPlan">
      <Card.Header className="BillingPlan__Header">
        <span>{header}</span>
        <CloseIcon onClick={onClose} className="BillingPlan__Header__Icon" />
      </Card.Header>
      <Card.Body className="BillingPlan__Body">{isCreditCard ? <UpdateBilling /> : <AddCreditCard />}</Card.Body>
    </Card>
  );
};
